import React, { useMemo } from 'react';
import { Popover, Progress, Spin } from 'antd';
import { InfoCircleOutlined, RadarChartOutlined } from '@ant-design/icons';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  MERTON_RISK_BANDS,
  resolveRiskBandFromPd,
  getDisplayRiskLabel,
} from '../../utils/mertonRiskBands';
import {
  formatDefaultRiskDate,
  getDefaultRiskColor,
  getDefaultRiskProgressPercent,
  getDefaultRiskChange,
  normalizeDefaultRiskData,
} from '../../utils/defaultRiskUtils';
import './DefaultRiskPredictor.css';

function DefaultRiskInfoContent({ data }) {
  const riskLabel = getDisplayRiskLabel(data);
  const pdBand = resolveRiskBandFromPd(data?.PD_percent);

  return (
    <div className="drp-info-popover">
      <p className="drp-info-lead">
        <strong>Default risk</strong> — market-implied chance the issuer may default. Lower % maps to a safer credit band.
      </p>
      <div className="drp-risk-legend">
        {MERTON_RISK_BANDS.map((band) => {
          const isActive = band.label === riskLabel || pdBand?.label === band.label;
          return (
            <div
              key={band.categoryKey}
              className={`drp-risk-legend-row${isActive ? ' active' : ''}`}
            >
              <span className="drp-risk-legend-label" style={{ color: getDefaultRiskColor(band.label) }}>
                {band.label}
              </span>
              <span className="drp-risk-legend-range">{band.range}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DefaultRiskPredictor({
  data,
  loading = false,
  showHeader = true,
  onChartClick,
  onInfoOpenChange,
  getInfoPopupContainer,
  className = '',
}) {
  const normalized = useMemo(() => normalizeDefaultRiskData(data), [data]);
  const pdHistory = normalized?.pdHistory ?? [];
  const pdChartX = pdHistory.map((row) => formatDefaultRiskDate(row.tradeDate));
  const pdChartY = pdHistory.map((row) => Number(row.PD_percent ?? 0));
  const pdChange = getDefaultRiskChange(pdHistory);
  const displayRisk = normalized ? getDisplayRiskLabel(normalized) : 'N/A';
  const riskColor = getDefaultRiskColor(displayRisk);
  const latestPd = normalized?.PD_percent;
  const pdDisplay = latestPd != null ? `${Number(latestPd).toFixed(2)}%` : 'N/A';
  const chartInteractive = Boolean(onChartClick && pdHistory.length > 0 && !loading);

  const chartPanelProps = chartInteractive
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: onChartClick,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChartClick();
          }
        },
      }
    : {};

  return (
    <div className={`drp-card ${className}`.trim()}>
      {showHeader && normalized && (
        <div className="drp-card-top">
          <div>
            <div className="drp-card-eyebrow">
              <RadarChartOutlined /> Issuer credit snapshot
            </div>
            <h4 className="drp-card-issuer" title={normalized.issuerName}>
              {normalized.issuerName || '—'}
            </h4>
            <p className="drp-card-date">Result as on {formatDefaultRiskDate(normalized.asOnDate)}</p>
            <p className="drp-card-summary">
              Current credit band alongside how default risk has evolved for this issuer.
            </p>
          </div>
          {pdChange != null && (
            <span
              className={`drp-change-pill${
                Math.abs(pdChange) < 0.005
                  ? ' neutral'
                  : pdChange < 0
                    ? ' improved'
                    : ' worsened'
              }`}
            >
              {Math.abs(pdChange) < 0.005
                ? 'Stable vs last result'
                : `Default risk ${pdChange < 0 ? 'down' : 'up'} ${Math.abs(pdChange).toFixed(2)}%`}
            </span>
          )}
        </div>
      )}

      <div className="drp-card-grid">
        <div className="drp-signal-panel">
          <div className="drp-signal-title-row">
            <div className="drp-signal-title">
              Default risk & credit band
              <Popover
                content={<DefaultRiskInfoContent data={normalized} />}
                title="Default risk"
                trigger={['hover', 'click']}
                placement="top"
                overlayClassName="drp-info-popover-overlay"
                onOpenChange={onInfoOpenChange}
                getPopupContainer={getInfoPopupContainer}
              >
                <button
                  type="button"
                  className="drp-metric-info-btn"
                  aria-label="What default risk means and how credit bands are set"
                  onClick={(event) => event.stopPropagation()}
                >
                  <InfoCircleOutlined />
                </button>
              </Popover>
            </div>
          </div>
          <div className="drp-signal-ring-wrap">
            {loading ? (
              <Spin />
            ) : normalized ? (
              <Progress
                type="circle"
                percent={getDefaultRiskProgressPercent(displayRisk)}
                strokeColor={riskColor}
                trailColor="#e2e8f0"
                strokeWidth={11}
                size={160}
                format={() => (
                  <span className="drp-risk-label-wrap">
                    <span className="drp-risk-pd-value">{pdDisplay}</span>
                    <span className="drp-risk-label-kicker">Default risk</span>
                    <span className="drp-risk-label-value" style={{ color: riskColor }}>
                      {displayRisk}
                    </span>
                  </span>
                )}
              />
            ) : (
              <div className="drp-empty-state">No default risk data available</div>
            )}
          </div>
        </div>

        <div
          className={`drp-chart-panel${chartInteractive ? ' drp-chart-panel--interactive' : ''}`}
          {...chartPanelProps}
        >
          <div className="drp-chart-header">
            <div>
              <div className="drp-chart-title">Default risk trend</div>
              <div className="drp-chart-subtitle">Historical path of estimated default likelihood</div>
            </div>
          </div>
          <div className="drp-chart-body">
            {loading ? (
              <div className="drp-chart-empty">
                <Spin />
              </div>
            ) : pdHistory.length > 0 ? (
              <LineChart
                xAxis={[{
                  scaleType: 'point',
                  data: pdChartX,
                  tickLabelStyle: { fontSize: 10, fill: '#64748b' },
                }]}
                yAxis={[{ min: 0, tickLabelStyle: { fill: '#64748b' } }]}
                series={[{
                  data: pdChartY,
                  label: 'Default risk (%)',
                  color: '#2563eb',
                  area: true,
                  showMark: false,
                  curve: 'monotoneX',
                }]}
                grid={{ horizontal: true, vertical: false }}
                height={220}
                margin={{ left: 44, right: 12, top: 8, bottom: 30 }}
                sx={{
                  '& .MuiChartsAxis-line': { stroke: 'rgba(100, 116, 139, 0.35)' },
                  '& .MuiChartsGrid-line': { stroke: 'rgba(100, 116, 139, 0.15)' },
                }}
              />
            ) : (
              <div className="drp-chart-empty">No historical default risk data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DefaultRiskPredictor;
